import { Injectable, signal, inject } from '@angular/core';
import { supabase } from '../supabase';
import { Professional, ProfessionalLocationLink, ProfessionalTeamLink, ProfessionalTeamQuota } from '../models';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';

@Injectable({
    providedIn: 'root'
})
export class ProfessionalService {
    private loadingService = inject(LoadingService);
    private toast = inject(ToastService);

    public professionalsTable = signal<Professional[]>([]);

    async refreshProfessionals(municipalityId: string | null) {
        if (!municipalityId) return;
        
        // Fetch professionals
        const { data: profs, error: profsError } = await supabase
            .from('professionals')
            .select('*')
            .eq('municipality_id', municipalityId);

        if (profsError) {
            console.error('[ProfessionalService] Error loading professionals:', profsError);
            return;
        }

        // Fetch links for the entire municipality (more efficient than looping)
        const { data: links, error: linksError } = await supabase
            .from('professional_locations')
            .select('*, professionals!inner(municipality_id)')
            .eq('professionals.municipality_id', municipalityId);

        if (linksError) {
            console.error('[ProfessionalService] Error loading professional links:', linksError);
            return;
        }

        // Fetch team links for the entire municipality
        const { data: teamLinks, error: teamLinksError } = await supabase
            .from('professional_teams')
            .select('*, professionals!inner(municipality_id)')
            .eq('professionals.municipality_id', municipalityId);

        if (teamLinksError) {
            console.error('[ProfessionalService] Error loading professional team links:', teamLinksError);
            return;
        }

        const mappedProfs: Professional[] = (profs || []).map(p => ({
            id: p.id,
            municipalityId: p.municipality_id,
            locationId: p.location_id,
            name: p.name,
            councilNumber: p.council_number,
            profession: p.profession,
            isActive: p.is_active,
            locationIds: (links || [])
                .filter(l => l.professional_id === p.id)
                .map(l => l.location_id),
            teamIds: (teamLinks || [])
                .filter(tl => tl.professional_id === p.id)
                .map(tl => tl.team_id)
        }));

        this.professionalsTable.set(mappedProfs);
    }

    async addProfessional(prof: Partial<Professional>) {
        const primaryLocationId = prof.locationId || (prof.locationIds && prof.locationIds.length > 0 ? prof.locationIds[0] : null);
        const { data, error } = await supabase
            .from('professionals')
            .insert([{
                municipality_id: prof.municipalityId,
                location_id: primaryLocationId,
                name: prof.name?.toUpperCase(),
                council_number: prof.councilNumber?.toUpperCase(),
                profession: prof.profession?.toUpperCase(),
                is_active: true
            }])
            .select()
            .single();

        if (error) throw error;
        
        // Add initial location if provided
        if (prof.locationIds && prof.locationIds.length > 0) {
            await this.updateProfessionalLocations(data.id, prof.locationIds);
        }

        await this.refreshProfessionals(prof.municipalityId!);
        return data;
    }

    async updateProfessional(id: string, updates: Partial<Professional>) {
        const { data, error } = await supabase
            .from('professionals')
            .update({
                name: updates.name?.toUpperCase(),
                council_number: updates.councilNumber?.toUpperCase(),
                profession: updates.profession?.toUpperCase(),
                is_active: updates.isActive
            })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;

        if (updates.locationIds !== undefined) {
            await this.updateProfessionalLocations(id, updates.locationIds);
        }

        if (updates.municipalityId) {
            await this.refreshProfessionals(updates.municipalityId);
        }
        
        return data;
    }

    async deleteProfessional(id: string) {
        const { error } = await supabase
            .from('professionals')
            .delete()
            .eq('id', id);
        if (error) throw error;
    }

    private async updateProfessionalLocations(professionalId: string, locationIds: string[]) {
        // Clear existing links
        await supabase
            .from('professional_locations')
            .delete()
            .eq('professional_id', professionalId);

        // Add new links
        if (locationIds.length > 0) {
            const links = locationIds.map(locId => ({
                professional_id: professionalId,
                location_id: locId
            }));
            const { error } = await supabase
                .from('professional_locations')
                .insert(links);
            if (error) throw error;
        }
    }

    async getProfessionalTeams(professionalId: string): Promise<string[]> {
        const { data, error } = await supabase
            .from('professional_teams')
            .select('team_id')
            .eq('professional_id', professionalId);
        
        if (error) throw error;
        return (data || []).map(t => t.team_id);
    }

    async updateProfessionalTeams(professionalId: string, teamIds: string[]) {
        // Clear existing
        await supabase
            .from('professional_teams')
            .delete()
            .eq('professional_id', professionalId);
        
        // Add new
        if (teamIds && teamIds.length > 0) {
            const links = teamIds.map(tid => ({
                professional_id: professionalId,
                team_id: tid
            }));
            const { error } = await supabase
                .from('professional_teams')
                .insert(links);
            if (error) throw error;
        }
    }

    async getTeamProfessionals(teamId: string): Promise<string[]> {
        const { data, error } = await supabase
            .from('professional_teams')
            .select('professional_id')
            .eq('team_id', teamId);
        
        if (error) throw error;
        return (data || []).map(t => t.professional_id);
    }

    async updateTeamProfessionals(teamId: string, professionalIds: string[]) {
        // Clear existing
        await supabase
            .from('professional_teams')
            .delete()
            .eq('team_id', teamId);
        
        // Add new
        if (professionalIds && professionalIds.length > 0) {
            const links = professionalIds.map(pid => ({
                professional_id: pid,
                team_id: teamId
            }));
            const { error } = await supabase
                .from('professional_teams')
                .insert(links);
            if (error) throw error;
        }
    }

    async getProfessionalQuotas(locationId: string): Promise<ProfessionalTeamQuota[]> {
        const { data, error } = await supabase
            .from('professional_team_quotas')
            .select(`
                *,
                professionals(name),
                acs_teams(name)
            `)
            .eq('location_id', locationId);
        
        if (error) throw error;

        return (data || []).map(q => ({
            id: q.id,
            professionalId: q.professional_id,
            teamId: q.team_id,
            locationId: q.location_id,
            dailyQuota: q.daily_quota,
            professionalName: q.professionals?.name,
            teamName: q.acs_teams?.name || 'GERAL (UBS)'
        }));
    }

    async upsertProfessionalQuota(quota: Partial<ProfessionalTeamQuota>) {
        const { data, error } = await supabase
            .from('professional_team_quotas')
            .upsert({
                id: quota.id,
                professional_id: quota.professionalId,
                team_id: quota.teamId,
                location_id: quota.locationId,
                daily_quota: quota.dailyQuota,
                updated_at: new Date().toISOString()
            })
            .select()
            .single();
        
        if (error) throw error;
        return data;
    }

    async deleteProfessionalQuota(id: string) {
        const { error } = await supabase
            .from('professional_team_quotas')
            .delete()
            .eq('id', id);
        
        if (error) throw error;
    }
}

